import type { KpiStyle } from "../components/KpiCard.js";

/**
 * KPI Card spec — kpi.v1 (ADR-0070).
 *
 * Mirrors the hub-side `KpiCardVizSpecV1` so the inspector can author/edit a
 * KPI Card without round-tripping the spec through the hub. Stays in sync with
 * `atlas-hub/services/hub/src/reportRuntime/kpiCardSpec.ts`.
 */
export type KpiCardVizSpecV1 = {
  specVersion: "kpi.v1";
  kind: "kpi";
  items: Array<KpiCardItem>;
  style?: KpiStyle;
};

export type KpiCardItem = {
  field: string;
  aggregation: KpiAggregation;
  label?: string;
  format?: KpiCardFormat;
};

export type KpiAggregation = "sum" | "avg" | "min" | "max" | "count";

export type KpiCardFormat = {
  kind: "none" | "number" | "currency" | "percent";
  decimals?: number;
  currency?: string;
};

export const KPI_AGGREGATIONS: KpiAggregation[] = ["sum", "avg", "min", "max", "count"];
export const KPI_FORMAT_KINDS: KpiCardFormat["kind"][] = ["none", "number", "currency", "percent"];

/** Produce a fresh empty spec for `addNode("kpi_card")`. */
export function emptyKpiCardSpec(): KpiCardVizSpecV1 {
  return { specVersion: "kpi.v1", kind: "kpi", items: [] };
}

/**
 * Validate a candidate spec object. Returns either the normalized spec or
 * a list of human-readable error strings. Mirrors the hub-side sanitizer's
 * checks (the hub still validates on save — this is for inspector UX).
 */
export function validateKpiCardSpec(input: unknown): { ok: true; spec: KpiCardVizSpecV1 } | { ok: false; errors: string[] } {
  const errors: string[] = [];
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return { ok: false, errors: ["KPI Card spec must be an object"] };
  }
  const obj = input as Record<string, unknown>;
  if (obj.specVersion !== "kpi.v1") errors.push('specVersion must be "kpi.v1"');
  if (obj.kind !== "kpi") errors.push('kind must be "kpi"');
  if (!Array.isArray(obj.items)) {
    return { ok: false, errors: [...errors, "items must be an array"] };
  }
  if (obj.items.length === 0) errors.push("items must have at least one entry");

  const items: KpiCardItem[] = [];
  for (let i = 0; i < obj.items.length; i++) {
    const raw = obj.items[i];
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      errors.push(`items[${i}] must be an object`);
      continue;
    }
    const it = raw as Record<string, unknown>;
    const field = typeof it.field === "string" ? it.field.trim() : "";
    if (!field) errors.push(`items[${i}].field is required`);
    const agg = typeof it.aggregation === "string" ? it.aggregation : "";
    if (!KPI_AGGREGATIONS.includes(agg as KpiAggregation)) {
      errors.push(`items[${i}].aggregation must be one of: ${KPI_AGGREGATIONS.join(", ")}`);
    }
    const label = typeof it.label === "string" ? it.label : undefined;
    const format = parseFormat(it.format, i, errors);
    items.push({ field, aggregation: agg as KpiAggregation, label, format });
  }

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    spec: {
      specVersion: "kpi.v1",
      kind: "kpi",
      items,
      style: parseStyle(obj.style),
    },
  };
}

function parseFormat(raw: unknown, idx: number, errors: string[]): KpiCardFormat | undefined {
  if (raw == null) return undefined;
  if (typeof raw !== "object" || Array.isArray(raw)) {
    errors.push(`items[${idx}].format must be an object`);
    return undefined;
  }
  const f = raw as Record<string, unknown>;
  const kind = typeof f.kind === "string" ? f.kind : "";
  if (!KPI_FORMAT_KINDS.includes(kind as KpiCardFormat["kind"])) {
    errors.push(`items[${idx}].format.kind must be one of: ${KPI_FORMAT_KINDS.join(", ")}`);
    return undefined;
  }
  let decimals: number | undefined;
  if (f.decimals !== undefined && f.decimals !== null) {
    if (typeof f.decimals !== "number" || !Number.isInteger(f.decimals) || f.decimals < 0 || f.decimals > 6) {
      errors.push(`items[${idx}].format.decimals must be an integer between 0 and 6`);
    } else {
      decimals = f.decimals;
    }
  }
  let currency: string | undefined;
  if (kind === "currency") {
    if (f.currency === undefined || f.currency === null || f.currency === "") {
      currency = "USD";
    } else if (typeof f.currency !== "string" || f.currency.length !== 3) {
      errors.push(`items[${idx}].format.currency must be a 3-letter ISO code`);
    } else {
      currency = f.currency;
    }
  }
  return { kind: kind as KpiCardFormat["kind"], decimals, currency };
}

function parseStyle(raw: unknown): KpiStyle | undefined {
  if (raw == null) return undefined;
  if (typeof raw !== "object" || Array.isArray(raw)) return undefined;
  // Pass through — the inspector renders against KpiStyle directly. Range
  // clamping happens server-side in the hub sanitizer.
  return raw as KpiStyle;
}

/**
 * Render-time adapter: wrap a KPI Card spec into the legacy
 * `{ option: { atlasKpi, atlasKpiStyle } }` shape that the existing
 * compileViz path expects. Mirrors hub-side `kpiCardSpecToVizSpec`.
 */
export function kpiCardSpecToLegacyOption(spec: KpiCardVizSpecV1): {
  option: { atlasKpi: KpiCardItem[]; atlasKpiStyle: KpiStyle | undefined };
} {
  return { option: { atlasKpi: spec.items, atlasKpiStyle: spec.style } };
}
